import { Button, Box, Divider } from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { EmptyState } from "../../components/EmptyState";
import { useProducts } from "../../features/products/product-func";
import { appFeatures } from "../../features/guide/features";
import { useAtom } from "jotai";
import { sessionAtom } from "../../features/accounts/account.state";
import { SHOPIFY_GATEWAY } from "../../features/accounts/account";
import { trackEvent } from "../../features/analytics/analytics";

type Props = {
  onAdd: () => void;
  onCsvImport?: () => void;
  onSample?: () => void;
};

export const EmptyProductDb = ({ onAdd, onCsvImport, onSample }: Props) => {
  const { isReadOnly } = useProducts();
  const [session] = useAtom(sessionAtom);
  const navigate = useNavigate();
  const isShopifyUser = session.account.gateway === SHOPIFY_GATEWAY;

  const handleImportClick = (source: string) => {
    trackEvent({
      type: "trace",
      category: "empty-product-db",
      action: "import-click",
      label: source,
      internal: true,
    });
  };

  return (
    <EmptyState
      label="Add your first product!"
      imageSrc="/images/open-box.png"
      helpUrl={appFeatures.products.helpUrl}
    >
      {!isReadOnly && (
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            flexWrap: "wrap",
            justifyContent: "center",
            alignItems: "center",
            mt: 2,
          }}
        >
          {/* AI Concierge - primary option */}
          <Button
            variant="contained"
            size="small"
            component={RouterLink}
            to={appFeatures.concierge.path}
            onClick={() => handleImportClick("ai-concierge")}
            startIcon={<appFeatures.concierge.icon />}
          >
            AI Import
          </Button>

          {/* Divider */}
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          {/* Other import options */}
          <Button
            variant={isShopifyUser ? "outlined" : "text"}
            size="small"
            component={RouterLink}
            to={appFeatures.shopify.path}
            onClick={() => handleImportClick("shopify")}
          >
            Shopify
          </Button>
          <Button
            variant="text"
            size="small"
            onClick={() => {
              handleImportClick("csv");
              if (onCsvImport) {
                onCsvImport();
              } else {
                navigate(appFeatures.csv.path);
              }
            }}
          >
            CSV / Excel
          </Button>
          <Button
            variant="text"
            size="small"
            component={RouterLink}
            to={appFeatures.productUpload.path}
            onClick={() => handleImportClick("upload")}
          >
            Bulk Upload
          </Button>

          {/* Divider */}
          <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

          {/* Manual options - secondary */}
          <Button
            variant="text"
            size="small"
            onClick={() => {
              trackEvent({
                type: "trace",
                category: "empty-product-db",
                action: "manual-click",
                label: "add-product",
                internal: true,
              });
              onAdd();
            }}
          >
            Add Manually
          </Button>
          {onSample && (
            <Button
              variant="text"
              size="small"
              color="secondary"
              onClick={() => {
                trackEvent({
                  type: "trace",
                  category: "empty-product-db",
                  action: "sample-click",
                  label: "catalog-templates",
                  internal: true,
                });
                onSample();
              }}
            >
              Try Sample Catalog
            </Button>
          )}
        </Box>
      )}
    </EmptyState>
  );
};
